"use client";

import { Button } from "@pex/ui-web";
import { motion, useScroll, useTransform } from "framer-motion";
import Image from "next/image";
import { useEffect, useState } from "react";

interface PhotoConfig {
  alt: string;
  src: string;
  zIndex: number;
}

const PHOTOS: PhotoConfig[] = [
  {
    src: "/images/home/Home_Image01.webp",
    alt: "A man with a beard and moustache, wearing a hat, is smiling while holding up his index finger.",
    zIndex: 50,
  },
  {
    src: "/images/home/Home_Image02.webp",
    alt: "",
    zIndex: 10,
  },
  {
    src: "/images/home/Home_Image03.webp",
    alt: "",
    zIndex: 30,
  },
  {
    src: "/images/home/Home_Image05.webp",
    alt: "",
    zIndex: 40,
  },
  {
    src: "/images/home/Home_Image04.webp",
    alt: "",
    zIndex: 20,
  },
];

export function FindHelpHeroSection() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 479px)");
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const { scrollY } = useScroll();

  const halfViewportHeight =
    typeof window !== "undefined" ? window.innerHeight * 0.5 : 400;
  const viewportHeight =
    typeof window !== "undefined" ? window.innerHeight : 800;

  // Text container — fades on scroll (verbatim Relume Layout421, minus the
  // scale animation which dropped one MotionValue with no visible loss).
  const textOpacity = useTransform(scrollY, [0, halfViewportHeight], [1, 0]);

  // Image fan-out — x and y are scroll-driven so the photos spread apart on
  // scroll. Rotation is intentionally static (uses the end-state angle for
  // each image) so every photo carries the fan-out tilt at all times,
  // including at rest. Trades 8 per-frame MotionValue updates for 0.
  // images[0] has no transform (centre anchor stays put).
  const img1X = useTransform(
    scrollY,
    [0, viewportHeight],
    isMobile ? ["13%", "90%"] : ["0%", "100%"]
  );
  const img1Y = useTransform(
    scrollY,
    [0, viewportHeight],
    isMobile ? ["12%", "80%"] : ["0%", "60%"]
  );

  const img2X = useTransform(
    scrollY,
    [0, viewportHeight],
    isMobile ? ["-12%", "-80%"] : ["0%", "-50%"]
  );
  const img2Y = useTransform(
    scrollY,
    [0, viewportHeight],
    isMobile ? ["-12%", "-80%"] : ["0%", "-90%"]
  );

  const img3X = useTransform(
    scrollY,
    [0, viewportHeight],
    isMobile ? ["17.5%", "120%"] : ["0%", "140%"]
  );
  const img3Y = useTransform(
    scrollY,
    [0, viewportHeight],
    isMobile ? ["-6%", "-40%"] : ["0%", "-40%"]
  );

  const img4X = useTransform(
    scrollY,
    [0, viewportHeight],
    isMobile ? ["-17.5%", "-120%"] : ["0%", "-140%"]
  );
  const img4Y = useTransform(
    scrollY,
    [0, viewportHeight],
    isMobile ? ["9%", "60%"] : ["0%", "60%"]
  );

  const imageTransforms = [
    null,
    { x: img1X, y: img1Y, rotate: isMobile ? "4deg" : "-4deg" },
    { x: img2X, y: img2Y, rotate: "4deg" },
    { x: img3X, y: img3Y, rotate: isMobile ? "-6deg" : "-12deg" },
    { x: img4X, y: img4Y, rotate: "8deg" },
  ];

  return (
    <section className="relative flex flex-col overflow-x-clip pb-[20vw] md:pb-[14vw] lg:pb-[max(8vw,calc(33vw_-_60vh_+_8vw))]">
      {/* Text — sticky, fades out on scroll (verbatim Relume Layout421) */}
      <motion.div
        className="relative sticky top-0 z-0 flex min-h-0 w-full items-center justify-center md:min-h-[auto]"
        style={{ opacity: textOpacity }}
      >
        {/* Orange blob decorations — sit behind text, fade with it */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 -left-[22vw] z-0 hidden h-[60vw] max-h-[600px] w-[52vw] max-w-[520px] -translate-y-1/2 opacity-40 md:block"
          style={{
            borderRadius: "62% 38% 46% 54% / 51% 65% 35% 49%",
            background:
              "radial-gradient(ellipse at 65% 45%, #F59340 0%, #E86020 55%, transparent 80%)",
            filter: "blur(72px)",
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 -right-[20vw] z-0 hidden h-[50vw] max-h-[520px] w-[58vw] max-w-[580px] -translate-y-1/2 opacity-30 md:block"
          style={{
            borderRadius: "44% 56% 67% 33% / 58% 42% 60% 40%",
            background:
              "radial-gradient(ellipse at 35% 55%, #F8A855 0%, #E86020 50%, transparent 78%)",
            filter: "blur(88px)",
          }}
        />
        <div className="relative z-10 py-16 text-center md:py-24 lg:py-28">
          <div className="mx-auto w-full max-w-2xl px-[5%]">
            <h1 className="heading-1 mb-5 font-bold md:mb-6">
              Your Journey of{" "}
              <span className="font-serif italic">Radical Change</span> Starts
              Here
            </h1>
            <p className="body-md relative z-20 max-w-[560px] text-muted-foreground">
              We connect you with dynamic addiction and supporter recovery
              solutions that meet you where you are and help you get to where
              you want to be. Begin renewing your mind and transforming your
              life by joining a recovery group or specialised programme today.
            </p>
            <div className="relative z-20 mt-6 flex w-full flex-col items-center justify-center gap-3 sm:w-auto sm:flex-row md:mt-8">
              <Button asChild className="w-full sm:w-auto" variant="fancy">
                <a
                  href={`${process.env.NEXT_PUBLIC_FINDER_URL}`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Find a Group
                </a>
              </Button>
              <Button
                asChild
                className="w-full sm:w-auto"
                variant="secondary-fancy"
              >
                <a
                  href={`${process.env.NEXT_PUBLIC_FINDER_URL}`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Join a Programme
                </a>
              </Button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Images — sticky, fans out on scroll (verbatim Relume Layout421) */}
      <div className="sticky top-0 z-10 -mt-20 flex h-[60svh] flex-col justify-center sm:mt-0 sm:h-[80svh] md:h-[70svh] lg:h-[120vh] lg:justify-normal">
        <div className="relative flex size-full items-center justify-center">
          {PHOTOS.map((photo, index) => {
            const transforms = imageTransforms[index];
            return (
              <motion.div
                className="absolute w-full max-w-[55vw] md:max-w-[35vw] lg:max-w-[30vw]"
                key={photo.src}
                style={{
                  ...(transforms !== null ? transforms : {}),
                  zIndex: photo.zIndex,
                  // Promote each animated image to its own compositor layer so
                  // scroll-linked transforms stay off the main thread on
                  // weaker mobile GPUs (Dimensity 720 / Samsung A32 5G class).
                  ...(transforms !== null ? { willChange: "transform" } : {}),
                }}
              >
                <div className="relative aspect-square overflow-hidden rounded-lg md:shadow-xl">
                  <Image
                    alt={photo.alt}
                    className="object-cover"
                    fill
                    priority
                    sizes="(max-width: 640px) 55vw, (max-width: 1024px) 35vw, 30vw"
                    src={photo.src}
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
